import type { BrandStore } from '@jotta/ui/BrandStore'
import { range } from '@jotta/utils/range'
import Debug from 'debug'
import { isObservableObject, makeAutoObservable } from 'mobx'
import { computedFn } from 'mobx-utils'
import type { CSSProperties } from 'react'
import type { VirtualListOptions } from './useVirtualList'

export interface VirtualListRowDataOptions<T> {
  items: readonly T[]
  columns: number
  rowHeight: number
  firstItemOffset: number
}
export interface VirtualListRowDataItemOptions<T> {
  index: number
  item: T
  columns: number
  rowHeight: number
  firstItemOffset: number
}
export type VirtualListRowData<T> = ReturnType<
  VirtualListStore<T>['getItemRowData']
>

export class VirtualListStore<T> {
  log = Debug('jotta:virtual:VirtualListStore')
  public options: VirtualListOptions<T>

  getRowData = computedFn((i: number) => {
    const c = this.columns
    const { firstItemOffset, rowHeight } = this
    const column = c > 1 ? i % c : 0
    const row = c > 1 ? (i ? Math.floor(i / c) : 0) : i
    const top = row * rowHeight + firstItemOffset
    const left = `${column * 100}%`
    const style: CSSProperties = {
      transform: `translate(${left}, ${top}px)`,
    }
    const key = `virtual-${i}-${column}-${row}`
    return {
      column,
      row,
      top,
      left,
      style,
      key,
    }
  })
  getItemRowData = computedFn((index: number, item?: T) => {
    const data = this.getRowData(index)
    if (!item) {
      return {
        type: 'empty',
        id: undefined,
        item: undefined,
        ...data,
      }
    }
    const id = item[this.options.idKey]
    return {
      type: 'item',
      id,
      item,
      ...data,
    }
  })

  constructor(
    private branding: BrandStore,
    options: VirtualListOptions<T>,
  ) {
    // this.log('Constructor')
    if (!isObservableObject(options)) {
      throw new Error('Options must be a mobx observable')
    }
    this.options = options
    makeAutoObservable(this, {
      options: false,
      log: false,
    })
  }
  get scrollInfo() {
    return this.options.scrollObservable || this.branding
  }

  get viewport() {
    return this.scrollInfo.rect
  }
  get scroll() {
    return this.scrollInfo.scroll
  }

  /**
   * Size added to visible area to render items slightly before they appear
   * Defaults to height of viewport (half added before, and half after)
   */
  get overscan() {
    return this.viewport.height
  }

  get cssCustomProperties(): CSSProperties {
    // this.log('update properties')
    return {
      '--virtual-columns': this.columns,
      '--virtual-list-height': `${this.totalHeight}px`,
      '--virtual-item-height': `${this.rowHeight}px`,
    } as CSSProperties
  }

  /**
   * Number of rows
   */
  get size() {
    return this.options.items.length
  }
  /**
   * Number of pixels to offset first row
   */
  get firstItemOffset() {
    return this.options.firstItemOffset || 0
  }

  /**
   * Minimum amount of columns to render
   */
  get minColumns() {
    return this.options.minColumns || 1
  }
  /**
   * Number of columns in grid
   * Should be as many as can fit the viewport based on options.columnWidth,
   * but never less than options.minColumns
   */
  get columns() {
    const { columnWidth } = this.options
    if (!columnWidth) {
      return this.minColumns
    }

    return Math.max(
      this.minColumns,
      Math.round(this.viewport.width / columnWidth),
    )
  }
  /**
   * The actual width of one column
   */
  get columnWidth() {
    return this.viewport.width / this.columns
  }
  /**
   * Row height based on options.rowHeight or options.cellAspect
   */
  get rowHeight() {
    return (
      this.options.rowHeight ||
      Math.round(this.columnWidth * (this.options.cellAspect || 0.5))
    )
  }
  /**
   * Number of rows in grid
   */
  get rows() {
    return Math.ceil(this.size / this.columns)
  }
  /**
   * Total height of grid
   */
  get totalHeight() {
    return this.rowHeight * this.rows + this.firstItemOffset
  }
  /**
   * Visible rows
   */
  get numberOfVisibleRows() {
    const height = this.viewport.height + this.overscan
    if (!height) {
      return 0
    }

    return Math.ceil(height / this.rowHeight) + 1
  }
  get firstVisibleRow() {
    if (!this.scroll.top) {
      return 0
    }
    const y = Math.floor(
      Math.max(0, this.scroll.top - this.overscan / 2) / this.rowHeight,
    )
    // this.log(
    //   'firstVisibleRow',
    //   this.viewport.scrollTop,
    //   this.viewport.height,
    //   y,
    // )
    return y
  }
  get lastVisibleRow() {
    return Math.min(this.rows, this.firstVisibleRow + this.numberOfVisibleRows)
  }
  get firstVisibleIndex() {
    return this.firstVisibleRow * this.columns
  }
  get lastVisibleIndex() {
    return this.lastVisibleRow * this.columns
  }
  get rowDataOptions(): VirtualListRowDataOptions<T> {
    return {
      items: this.options.items,
      columns: this.columns,
      rowHeight: this.rowHeight,
      firstItemOffset: this.firstItemOffset,
    }
  }
  getItemByIndex(i: number) {
    if (i < this.options.items.length) {
      return this.options.items[i]
    }
  }
  get rowData() {
    const items = range(0, this.lastVisibleIndex).map(i =>
      this.getItemRowData(i, this.getItemByIndex(i)),
    )
    // this.log(
    //   'rowData: %d of %d items, ',
    //   items.length,
    //   this.options.items.length,
    // )
    return items
  }

  get virtualRows() {
    // this.log('virtualRows %d-%d', this.firstVisibleIndex, this.lastVisibleIndex)
    const rows = this.rowData.slice(
      this.firstVisibleIndex,
      this.lastVisibleIndex + 1,
    )
    return rows
  }

  get debugInfo(): Record<string, string | number> {
    return {
      columns: this.columns,
      columnWidth: this.columnWidth,
      rowHeight: this.rowHeight,
      totalHeight: this.totalHeight,
      'Displaying rows': `${this.firstVisibleRow}-${this.lastVisibleRow} of ${this.rows}`,
      'Visible items': `${this.virtualRows.length} of ${this.size}`,
      ...this.viewport,
      ...this.scroll,
    }
  }
}
